import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "@/api/ux";
import { Button, Card } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  NotificationDispatchDiagnosticsCard,
  useCanSeeDispatchDiagnostics,
} from "@/components/notifications/NotificationDispatchDiagnosticsCard";
import type { NotificationCategory, NotificationChannel, NotificationPreferenceDto } from "@/types/api";

interface Row {
  category: NotificationCategory;
  channel: NotificationChannel;
  /** A per-event opt-in riding the pair above (D14). The server keys the row on
   *  all three and accepts only the codes it serves, so this must match the
   *  code the honouring worker reads (`NotificationEventCodes`, C#). */
  eventCode?: string;
  label: string;
  description: string;
}

/** The D14 buyer digest — `NotificationEventCodes.BuyerDailyDigest` in C#. */
export const BUYER_DAILY_DIGEST_EVENT_CODE = "buyer.daily-digest";

/** One key per (category, channel, eventCode) — the server's own key. */
function rowKey(r: { category: string; channel: string; eventCode?: string | null }): string {
  return `${r.category}:${r.channel}:${r.eventCode ?? ""}`;
}

const ROWS: Row[] = [
  {
    category: "Urgent",
    channel: "InApp",
    label: "Urgent · in-app bell",
    description:
      "Deal stage changes, compliance breaches, E&O expirations, default triggers, covenant breaches. Always-on for in-app delivery.",
  },
  {
    category: "Urgent",
    channel: "Email",
    label: "Urgent · email",
    description: "Mirror urgent notifications to your email for after-hours awareness.",
  },
  {
    category: "Info",
    channel: "InApp",
    label: "Info · in-app bell",
    description: "Document uploads, notes added, new assignments, report generated.",
  },
  {
    category: "Info",
    channel: "Email",
    label: "Info · email",
    description: "Mirror informational notifications to your email.",
  },
  {
    category: "Digest",
    channel: "Email",
    label: "Digest · daily email",
    description:
      "Once-a-day summary of non-urgent activity on watched or assigned items.",
  },
  {
    category: "Digest",
    channel: "Email",
    eventCode: BUYER_DAILY_DIGEST_EVENT_CODE,
    label: "Buyer digest · morning email",
    description:
      "A 7am (Phoenix) email on your buyer pipeline: team managers get the roll-up (new leads yesterday, overdue follow-ups, stale buyers, budget pace this month); consultants get their needs-action list. Off by default, and nothing is sent when there is nothing to report.",
  },
];

export function NotificationPreferencesPage() {
  const queryClient = useQueryClient();
  // ADMIN-only, and gated the same way the server gates it: the endpoint
  // answers 403 for anyone else, so hiding the surface is what keeps a
  // non-admin from watching a request fail. An ADMIN-only panel on an existing
  // page rather than a route of its own — the EscrowWebhookDiagnosticsCard
  // precedent, and it needs no new permission, route or sidebar entry.
  //
  // The predicate is the card's own, so the toggle can never offer a panel the
  // card would decline to render.
  const isAdmin = useCanSeeDispatchDiagnostics();
  // Behind a toggle, again per that precedent: the diagnostics read is a
  // group-by over every notification raised in the window, and it should not
  // run for every administrator who opens their own preferences.
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const prefs = useQuery({
    queryKey: ["notifications", "preferences"],
    queryFn: () => notificationsApi.getPreferences(),
  });

  const save = useMutation({
    mutationFn: (next: NotificationPreferenceDto[]) => notificationsApi.updatePreferences(next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications", "preferences"] }),
  });

  const initial = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const p of prefs.data ?? []) {
      map.set(rowKey(p), p.enabled);
    }
    return map;
  }, [prefs.data]);

  const [state, setState] = useState<Map<string, boolean>>(new Map());
  useEffect(() => {
    setState(new Map(initial));
  }, [initial]);

  const toggle = (row: Row) => {
    const key = rowKey(row);
    const next = new Map(state);
    next.set(key, !next.get(key));
    setState(next);
  };

  // What a row shows: the local edit, else what the server sent. A row the
  // server did not send reads unchecked, the fallback the checkbox always used.
  const displayed = (m: Map<string, boolean>, key: string) => m.get(key) ?? false;

  // Only the rows that differ from what the server sent. Posting every row
  // stored the day's defaults as though they were choices, so anyone who had
  // ever saved stopped following a default that later changed. The server
  // upserts exactly the rows it is sent and leaves the rest alone, so a row
  // nobody changed keeps following its default. Compared by value, not by
  // "was clicked": a row toggled and toggled back is not a change.
  const changed = ROWS.filter((r) => displayed(state, rowKey(r)) !== displayed(initial, rowKey(r)));

  const loaded = prefs.data !== undefined;
  // No data means nothing to show and nothing to save. A failed load used to
  // fall through to the rows with every box unchecked and Save enabled, so a
  // single click wrote "off" for every row.
  const loadFailed = !loaded && prefs.isError;

  const onSave = () => {
    const payload: NotificationPreferenceDto[] = changed.map((r) => ({
      category: r.category,
      channel: r.channel,
      enabled: displayed(state, rowKey(r)),
      ...(r.eventCode ? { eventCode: r.eventCode } : {}),
    }));
    save.mutate(payload);
  };

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Notification preferences"
        description="Choose which events reach you in-app and by email. Urgent alerts always surface in-app."
        actions={
          isAdmin ? (
            <Button variant="tertiary" size="sm" onClick={() => setShowDiagnostics((v) => !v)}>
              {showDiagnostics ? "Hide dispatch diagnostics" : "Email dispatch diagnostics"}
            </Button>
          ) : undefined
        }
      />
      {/* Self-capped: a single-column form region, and the shell's own cap
          (1600px) no longer bounds it usefully. `max-w-3xl` keeps the label and
          its toggle in the same glance instead of a 1536px row apart. See
          `AppShell.tsx`. */}
      <Card className="flex flex-col gap-space-3 max-w-3xl">
        {/* Gated on data, never on `isLoading`: that flag is false for a
            failed load AND for a paused (offline) query, and both used to reach
            the rows below with nothing behind them. */}
        {!loaded && !loadFailed && (
          <div className="text-label uppercase tracking-label text-gray-mid">Loading…</div>
        )}
        {loadFailed && (
          <p role="alert" className="text-small text-status-error m-0">
            We couldn't load your notification preferences, so there is nothing to show or save yet.
          </p>
        )}
        {loaded &&
          ROWS.map((row) => {
            const key = rowKey(row);
            const checked = state.get(key) ?? false;
            const forceOn = row.category === "Urgent" && row.channel === "InApp" && !row.eventCode;
            return (
              <label
                key={key}
                className="flex items-start gap-space-3 py-space-3 border-b border-border-light last:border-b-0 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="mt-[6px] h-4 w-4 accent-[var(--gold)]"
                  checked={forceOn ? true : checked}
                  disabled={forceOn}
                  onChange={() => !forceOn && toggle(row)}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-small text-onyx">{row.label}</div>
                  <p className="text-label text-gray-mid m-0 mt-[2px]">{row.description}</p>
                  {forceOn && (
                    <p className="text-label text-gold-shadow m-0 mt-[2px]">
                      Always on — Urgent in-app alerts can't be disabled.
                    </p>
                  )}
                </div>
              </label>
            );
          })}
        <div className="flex items-center gap-space-2 mt-space-2">
          {loadFailed ? (
            <Button
              variant="secondary"
              onClick={() => void prefs.refetch()}
              loading={prefs.isFetching}
            >
              Try again
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={onSave}
              disabled={save.isPending || !loaded || changed.length === 0}
            >
              {save.isPending ? "Saving…" : "Save preferences"}
            </Button>
          )}
          {save.isSuccess && (
            <span className="text-label text-gold-shadow font-display font-semibold uppercase tracking-label">
              Saved
            </span>
          )}
          {save.isError && (
            <span className="text-label text-status-error">
              {(save.error as Error).message}
            </span>
          )}
        </div>
      </Card>
      {isAdmin && showDiagnostics && (
        <div className="mt-space-4">
          <NotificationDispatchDiagnosticsCard />
        </div>
      )}
    </>
  );
}
